import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, Save, Wand2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";

function computeCorrect(q) {
  let v = Number(q.starting_number || 0);
  (q.operations || []).forEach((o) => {
    const val = Number(o.value || 0);
    if (o.op === "+") v = v + val;
    else if (o.op === "-") v = v - val;
    else if (o.op === "×") v = v * val;
    else if (o.op === "÷") v = val !== 0 ? v / val : v;
  });
  return v;
}

function newQuestion() {
  return { starting_number: 0, operations: [{ op: "+", value: 0 }] };
}

function randInt(min, max) {
  min = Math.ceil(min); max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateQuestions({ count, startMin, startMax, stepsMin, stepsMax, valMin, valMax, opsMode, nonNegative }) {
  const qs = [];
  for (let i = 0; i < count; i++) {
    let attempts = 0;
    while (attempts < 40) {
      attempts++;
      const start = randInt(startMin, startMax);
      const steps = randInt(stepsMin, stepsMax);
      const operations = [];
      let running = start;
      let ok = true;
      for (let s = 0; s < steps; s++) {
        let op;
        if (opsMode === "add") op = "+";
        else if (opsMode === "sub") op = "-";
        else if (opsMode === "mul") op = "×";
        else if (opsMode === "div") op = "÷";
        else if (opsMode === "all") op = ["+", "-", "×", "÷"][randInt(0, 3)];
        else op = Math.random() < 0.5 ? "+" : "-";
        let value = randInt(valMin, valMax);
        if (op === "÷") {
          const divisors = [];
          for (let d = Math.max(2, valMin); d <= Math.min(valMax, Math.abs(running)); d++) {
            if (running % d === 0) divisors.push(d);
          }
          if (divisors.length === 0) { op = Math.random() < 0.5 ? "+" : "-"; value = randInt(valMin, valMax); }
          else value = divisors[randInt(0, divisors.length - 1)];
        }
        if (nonNegative && op === "-" && running - value < 0) {
          // try flipping to +
          op = "+";
        }
        if (op === "+") running = running + value;
        else if (op === "-") running = running - value;
        else if (op === "×") running = running * value;
        else running = running / value;
        if (nonNegative && running < 0) { ok = false; break; }
        operations.push({ op, value });
      }
      if (ok) { qs.push({ starting_number: start, operations }); break; }
    }
  }
  return qs;
}

function GeneratorPanel({ onAppend, onReplace }) {
  const [config, setConfig] = useState({
    count: 10, startMin: 2, startMax: 9, stepsMin: 2, stepsMax: 3,
    valMin: 1, valMax: 9, opsMode: "both", nonNegative: true,
  });
  const [open, setOpen] = useState(false);

  const update = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const run = (mode) => {
    const cfg = {
      count: Number(config.count), startMin: Number(config.startMin), startMax: Number(config.startMax),
      stepsMin: Number(config.stepsMin), stepsMax: Number(config.stepsMax),
      valMin: Number(config.valMin), valMax: Number(config.valMax),
      opsMode: config.opsMode, nonNegative: config.nonNegative,
    };
    if (cfg.count < 1) return;
    const qs = generateQuestions(cfg);
    if (qs.length === 0) return;
    if (mode === "replace") onReplace(qs); else onAppend(qs);
  };

  return (
    <div className="mb-4 p-5 bg-sky-light border-2 border-sky/20 rounded-3xl">
      <button data-testid="toggle-generator" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 font-heading text-lg font-bold text-sky-hover">
          <Wand2 className="w-5 h-5" /> Auto-generate questions
        </span>
        <span className="text-sky-hover font-bold text-sm">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3" data-testid="generator-panel">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-bold text-stone uppercase mb-1">Count</label>
              <input data-testid="gen-count" type="number" min="1" max="50" value={config.count} onChange={(e) => update("count", e.target.value)} className="w-full h-10 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone uppercase mb-1">Operations</label>
              <select data-testid="gen-ops-mode" value={config.opsMode} onChange={(e) => update("opsMode", e.target.value)} className="w-full h-10 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-semibold">
                <option value="both">Add & Subtract</option>
                <option value="add">Add only</option>
                <option value="sub">Subtract only</option>
                <option value="mul">Multiply only</option>
                <option value="div">Divide only</option>
                <option value="all">All four (mixed)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone uppercase mb-1">Start range</label>
              <div className="flex items-center gap-2">
                <input data-testid="gen-start-min" type="number" value={config.startMin} onChange={(e) => update("startMin", e.target.value)} className="w-full h-10 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
                <span className="text-stone">–</span>
                <input data-testid="gen-start-max" type="number" value={config.startMax} onChange={(e) => update("startMax", e.target.value)} className="w-full h-10 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-stone uppercase mb-1">Steps per Q</label>
              <div className="flex items-center gap-2">
                <input data-testid="gen-steps-min" type="number" min="1" value={config.stepsMin} onChange={(e) => update("stepsMin", e.target.value)} className="w-full h-10 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
                <span className="text-stone">–</span>
                <input data-testid="gen-steps-max" type="number" min="1" value={config.stepsMax} onChange={(e) => update("stepsMax", e.target.value)} className="w-full h-10 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-stone uppercase mb-1">Value range (per step)</label>
              <div className="flex items-center gap-2">
                <input data-testid="gen-val-min" type="number" min="1" value={config.valMin} onChange={(e) => update("valMin", e.target.value)} className="w-full h-10 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
                <span className="text-stone">–</span>
                <input data-testid="gen-val-max" type="number" min="1" value={config.valMax} onChange={(e) => update("valMax", e.target.value)} className="w-full h-10 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
              </div>
            </div>
            <label className="sm:col-span-2 inline-flex items-center gap-2 mt-6">
              <input data-testid="gen-non-negative" type="checkbox" checked={config.nonNegative} onChange={(e) => update("nonNegative", e.target.checked)} className="w-5 h-5 accent-watermelon" />
              <span className="text-sm font-semibold text-ink">Prevent negative results</span>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="gen-append-button" type="button" onClick={() => run("append")} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-sky text-white font-bold btn-tactile hover:bg-sky-hover">
              <Wand2 className="w-4 h-4" /> Generate & append
            </button>
            <button data-testid="gen-replace-button" type="button" onClick={() => run("replace")} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white border-2 border-cloud text-ink font-bold btn-tactile hover:border-sky">
              Replace all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminExerciseEditor() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const isNew = !exerciseId;
  const [form, setForm] = useState({
    title: "",
    level: 1,
    week: 1,
    topic: "",
    description: "",
    type: "arithmetic",
    presentation_mode: "sequential",
    is_published: false,
    seconds_per_question: 30,
    max_attempts: 3,
    questions: [newQuestion()],
  });
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/exercises/${exerciseId}`);
        setForm({
          title: data.title,
          level: data.level,
          week: data.week,
          topic: data.topic,
          description: data.description || "",
          type: data.type || "arithmetic",
          presentation_mode: data.presentation_mode || "sequential",
          is_published: data.is_published,
          seconds_per_question: data.seconds_per_question ?? 30,
          max_attempts: data.max_attempts ?? 3,
          questions: (data.questions || []).map((q) => ({
            starting_number: q.starting_number,
            operations: q.operations,
          })),
        });
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    })();
  }, [exerciseId, isNew]);

  const setQ = (idx, updater) => {
    setForm((f) => ({ ...f, questions: f.questions.map((q, i) => (i === idx ? updater(q) : q)) }));
  };
  const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, newQuestion()] }));
  const removeQ = (idx) => setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));
  const moveQ = (idx, dir) => setForm((f) => {
    const next = [...f.questions];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return f;
    [next[idx], next[target]] = [next[target], next[idx]];
    return { ...f, questions: next };
  });
  const addOp = (idx) => setQ(idx, (q) => ({ ...q, operations: [...q.operations, { op: "+", value: 0 }] }));
  const removeOp = (qIdx, oIdx) => setQ(qIdx, (q) => ({ ...q, operations: q.operations.filter((_, i) => i !== oIdx) }));

  const save = async (publish = null) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        level: Number(form.level),
        week: Number(form.week),
        topic: form.topic.trim(),
        description: form.description.trim(),
        type: form.type,
        presentation_mode: form.presentation_mode,
        is_published: publish != null ? publish : form.is_published,
        questions: form.questions.map((q, i) => ({
          starting_number: Number(q.starting_number),
          operations: form.type === "arithmetic" || form.type === "assessment"
            ? q.operations.map((o) => ({ op: o.op, value: Number(o.value) }))
            : [],
          order: i + 1,
        })),
      };
      if (form.type === "assessment") {
        payload.seconds_per_question = Number(form.seconds_per_question) || 30;
        payload.max_attempts = Number(form.max_attempts) || 3;
      }
      if (!payload.title) { toast.error("Title required"); return; }
      if (payload.questions.length === 0) { toast.error("Add at least one question"); return; }
      if (isNew) {
        const { data } = await api.post("/admin/exercises", payload);
        toast.success("Exercise created");
        navigate(`/admin/exercises/${data.id}`, { replace: true });
      } else {
        await api.put(`/admin/exercises/${exerciseId}`, payload);
        toast.success("Exercise saved");
        if (publish != null) setForm((f) => ({ ...f, is_published: publish }));
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <AppLayout variant="admin">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button onClick={() => navigate("/admin/exercises")} className="text-sm font-bold text-stone hover:text-ink mb-4">← Back to exercises</button>
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-ink">{isNew ? "New Exercise" : "Edit Exercise"}</h1>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="preview-exercise-button" onClick={() => setPreviewing(true)} className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-white border-2 border-cloud font-bold text-ink btn-tactile"><Eye className="w-4 h-4" /> Preview</button>
            {!isNew && (
              <button
                data-testid="toggle-publish-button"
                onClick={() => save(!form.is_published)}
                className={`inline-flex items-center gap-2 h-11 px-4 rounded-full font-bold text-white btn-tactile ${form.is_published ? "bg-stone" : "bg-leaf hover:bg-leaf/90"}`}
              >
                {form.is_published ? "Unpublish" : "Publish"}
              </button>
            )}
            <button data-testid="save-exercise-button" onClick={() => save()} disabled={saving} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        <div className="p-6 bg-white rounded-3xl card-tactile mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold text-ink mb-1">Title</label>
              <input data-testid="exercise-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-1">Level</label>
              <input data-testid="exercise-level-input" type="number" min="1" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-1">Week</label>
              <input data-testid="exercise-week-input" type="number" min="1" value={form.week} onChange={(e) => setForm({ ...form, week: e.target.value })} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold text-ink mb-1">Topic</label>
              <input data-testid="exercise-topic-input" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold text-ink mb-1">Exercise type</label>
              <select
                data-testid="exercise-type-select"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full h-12 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-semibold"
              >
                <option value="arithmetic">Math drill (add/subtract sequence)</option>
                <option value="read">Show the number on the abacus</option>
                <option value="represent">Read the beads on the abacus</option>
                <option value="assessment">Assessment (timed level-end exam)</option>
              </select>
              <p className="text-xs text-stone mt-1">
                {form.type === "arithmetic" && "Students solve a vertical arithmetic sequence and type the result."}
                {form.type === "read" && "Students see a number and must set the beads on a 13-rod abacus to match."}
                {form.type === "represent" && "Students see beads already set on the abacus and must type the number."}
                {form.type === "assessment" && "Timed exam: each question has a countdown. Students get 3 attempts; best score is locked in."}
              </p>
            </div>
            {form.type !== "assessment" && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-bold text-ink mb-1">How students see it</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label className={`flex-1 flex items-start gap-3 p-3 rounded-2xl cursor-pointer border-2 ${form.presentation_mode === "sequential" ? "border-sky bg-sky-light/30" : "border-cloud bg-canvas"}`}>
                    <input data-testid="editor-mode-sequential" type="radio" name="editor-mode" value="sequential" checked={form.presentation_mode === "sequential"} onChange={() => setForm({ ...form, presentation_mode: "sequential" })} className="mt-1 w-4 h-4 accent-sky" />
                    <div>
                      <div className="font-bold text-ink text-sm">One at a time</div>
                      <div className="text-xs text-stone">Big question + abacus, Next button</div>
                    </div>
                  </label>
                  <label className={`flex-1 flex items-start gap-3 p-3 rounded-2xl cursor-pointer border-2 ${form.presentation_mode === "grid" ? "border-sky bg-sky-light/30" : "border-cloud bg-canvas"}`}>
                    <input data-testid="editor-mode-grid" type="radio" name="editor-mode" value="grid" checked={form.presentation_mode === "grid"} onChange={() => setForm({ ...form, presentation_mode: "grid" })} className="mt-1 w-4 h-4 accent-sky" />
                    <div>
                      <div className="font-bold text-ink text-sm">Worksheet grid</div>
                      <div className="text-xs text-stone">All questions on one page, type each answer, submit once — like the physical book</div>
                    </div>
                  </label>
                </div>
              </div>
            )}
            {form.type === "assessment" && (
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-lemon/25 border-2 border-lemon rounded-2xl">
                <div>
                  <label className="block text-sm font-bold text-ink mb-1">Seconds per question</label>
                  <input
                    data-testid="exercise-seconds-input"
                    type="number"
                    min="5"
                    max="600"
                    value={form.seconds_per_question ?? 30}
                    onChange={(e) => setForm({ ...form, seconds_per_question: e.target.value })}
                    className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none"
                  />
                  <p className="text-xs text-stone mt-1">Between 5 and 600 seconds. Auto-skips to next when timer hits 0.</p>
                </div>
                <div>
                  <label className="block text-sm font-bold text-ink mb-1">Max attempts</label>
                  <input
                    data-testid="exercise-max-attempts-input"
                    type="number"
                    min="1"
                    max="10"
                    value={form.max_attempts ?? 3}
                    onChange={(e) => setForm({ ...form, max_attempts: e.target.value })}
                    className="w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none"
                  />
                  <p className="text-xs text-stone mt-1">Best score is locked in after last attempt.</p>
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold text-ink mb-1">Description (optional)</label>
              <textarea data-testid="exercise-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full min-h-[80px] px-4 py-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
            </div>
          </div>
        </div>

        <h2 className="font-heading text-2xl font-bold text-ink mb-3">Questions</h2>
        <GeneratorPanel
          onAppend={(qs) => setForm((f) => ({ ...f, questions: [...f.questions, ...(f.type === "arithmetic" ? qs : qs.map((q) => ({ starting_number: q.starting_number, operations: [] })))] }))}
          onReplace={(qs) => setForm((f) => ({ ...f, questions: f.type === "arithmetic" ? qs : qs.map((q) => ({ starting_number: q.starting_number, operations: [] })) }))}
        />
        <div className="space-y-4">
          {form.questions.map((q, qIdx) => {
            const correct = computeCorrect(q);
            return (
              <div key={qIdx} data-testid={`question-editor-${qIdx}`} className="p-5 bg-white rounded-3xl card-tactile">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading text-lg font-bold text-ink">Q{qIdx + 1}</div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`move-up-${qIdx}`} onClick={() => moveQ(qIdx, -1)} className="p-2 rounded-full hover:bg-canvas text-stone"><ArrowUp className="w-4 h-4" /></button>
                    <button data-testid={`move-down-${qIdx}`} onClick={() => moveQ(qIdx, +1)} className="p-2 rounded-full hover:bg-canvas text-stone"><ArrowDown className="w-4 h-4" /></button>
                    <button data-testid={`delete-question-${qIdx}`} onClick={() => removeQ(qIdx)} className="p-2 rounded-full hover:bg-watermelon-light text-watermelon"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_160px] gap-3 items-start">
                  <div>
                    <label className="block text-xs font-bold text-stone uppercase mb-1">
                      {form.type === "arithmetic" ? "Start" : "Number"}
                    </label>
                    <input data-testid={`q-${qIdx}-start`} type="number" value={q.starting_number} onChange={(e) => setQ(qIdx, (o) => ({ ...o, starting_number: e.target.value }))} className="w-full h-11 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none" />
                  </div>
                  {form.type === "arithmetic" ? (
                    <div>
                      <label className="block text-xs font-bold text-stone uppercase mb-1">Operations</label>
                      <div className="space-y-2">
                        {q.operations.map((op, oIdx) => (
                          <div key={oIdx} className="flex items-center gap-2">
                            <select
                              data-testid={`q-${qIdx}-op-${oIdx}-sign`}
                              value={op.op}
                              onChange={(e) => setQ(qIdx, (Q) => ({ ...Q, operations: Q.operations.map((x, i) => i === oIdx ? { ...x, op: e.target.value } : x) }))}
                              className="h-11 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none font-bold"
                            >
                              <option value="+">+</option>
                              <option value="-">−</option>
                              <option value="×">×</option>
                              <option value="÷">÷</option>
                            </select>
                            <input
                              data-testid={`q-${qIdx}-op-${oIdx}-value`}
                              type="number"
                              value={op.value}
                              onChange={(e) => setQ(qIdx, (Q) => ({ ...Q, operations: Q.operations.map((x, i) => i === oIdx ? { ...x, value: e.target.value } : x) }))}
                              className="flex-1 h-11 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none"
                            />
                            <button data-testid={`q-${qIdx}-op-${oIdx}-remove`} onClick={() => removeOp(qIdx, oIdx)} className="p-2 rounded-full hover:bg-watermelon-light text-watermelon"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <button data-testid={`q-${qIdx}-add-op`} onClick={() => addOp(qIdx)} className="inline-flex items-center gap-1 text-sm font-bold text-sky hover:underline"><Plus className="w-4 h-4" /> Add step</button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-stone self-center">
                      {form.type === "read" ? "Students set this number on the abacus." : "This number is shown on the abacus; students type it."}
                    </div>
                  )}
                  <div className="text-right">
                    <div className="text-xs font-bold text-stone uppercase mb-1">Correct answer</div>
                    <div data-testid={`q-${qIdx}-correct`} className="font-heading text-3xl font-bold text-leaf">{form.type === "arithmetic" ? correct : Number(q.starting_number || 0)}</div>
                  </div>
                </div>
              </div>
            );
          })}
          <button data-testid="add-question-button" onClick={addQ} className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white border-2 border-dashed border-sky text-sky font-bold btn-tactile hover:bg-sky-light">
            <Plus className="w-5 h-5" /> Add Question
          </button>
        </div>

        {/* Preview */}
        {previewing && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPreviewing(false)}>
            <div className="bg-white rounded-3xl max-w-md w-full max-h-[80vh] overflow-y-auto p-6 card-tactile" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-heading text-2xl font-bold text-ink">{form.title || "Untitled"}</h3>
                <button onClick={() => setPreviewing(false)} className="text-stone">✕</button>
              </div>
              <div className="text-sm text-stone mb-4">Level {form.level} · Week {form.week} · {form.topic}</div>
              <div className="space-y-3">
                {form.questions.map((q, i) => (
                  <div key={i} className="p-4 bg-canvas rounded-2xl">
                    <div className="font-bold text-ink">Q{i + 1}</div>
                    <div className="font-heading text-2xl font-bold text-ink">
                      {q.starting_number}
                      {q.operations.map((op, j) => (
                        <span key={j}> <span className={op.op === "+" ? "text-leaf" : "text-watermelon"}>{op.op}</span>{op.value}</span>
                      ))}
                      <span className="text-stone"> = {computeCorrect(q)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
